import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, ArrowRight, Handshake, Building2, Sparkles, Star } from "lucide-react";
import logo from "@/assets/galahadji-logo.png";

const PLANS = [
  {
    id: "explorer", name: "Explorer", tagline: "Designed for new investors.",
    monthly: "$19.99", yearly: "$179.99", yearlyNote: "≈ $15/mo billed annually", annualSaving: "$59.89",
    features: ["Lead CRM", "Residential analyzer", "Basic repair calculator", "Offer tracker", "Seller intake", "100 saved leads", "10 deal analyses / month", "One user"],
    cta: "Start 7-day trial", accent: false,
  },
  {
    id: "professional", name: "Professional", tagline: "Our most popular plan.",
    monthly: "$49.99", yearly: "$499", yearlyNote: "≈ $41.58/mo billed annually", annualSaving: "$100.88",
    features: ["Unlimited leads", "Unlimited analyses", "Subject-To analyzer", "Seller-finance analyzer", "BRRRR analyzer", "Commercial & Multifamily", "Mobile home parks & Storage", "Document center", "County links", "PML / HML portal", "Team collaboration"],
    cta: "Start 7-day trial", accent: true, badge: "Most Popular",
  },
  {
    id: "enterprise", name: "Enterprise", tagline: "For teams and organizations.",
    monthly: "$99–149", yearly: "Custom", yearlyNote: "Volume & annual pricing",
    features: ["Multiple users", "Permission management", "Team dashboards", "API integrations", "Investor portal", "Custom branding", "Advanced reporting", "Priority support"],
    cta: "Talk to sales", accent: false, contact: true,
  },
];

function Nav() {
  return (
    <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-white/70 border-b border-border">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="pricing-header-logo" className="flex items-center gap-2"><img src={logo} alt="Galahadji Acquisitions" className="h-14 w-auto object-contain" /></Link>
        <nav className="flex items-center gap-6 text-sm">
          <Link to="/pricing" className="text-foreground font-medium">Pricing</Link>
          <Link to="/login" data-testid="pricing-login-link" className="text-muted-foreground hover:text-foreground transition-colors">Team Login</Link>
          <Link to="/seller-intake" className="bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium hover:bg-primary/90 transition-colors">Sell Your Property</Link>
        </nav>
      </div>
    </header>
  );
}

export default function Pricing() {
  const [annual, setAnnual] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const { user, checking } = useAuth();
  const nav = useNavigate();

  const startCheckout = async (p) => {
    if (checking) return;
    if (!user) {
      toast.message("Please sign in to start your subscription");
      nav("/login", { state: { from: { pathname: "/pricing" } } });
      return;
    }
    const lookup = `${p.id}_${annual ? "yearly" : "monthly"}`;
    setLoadingPlan(p.id);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lookup });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Nav />

      <section className="relative pt-32 pb-16 grain overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-accent uppercase tracking-[0.3em] text-xs font-medium">Pricing</span>
            <h1 className="font-marketing text-4xl sm:text-5xl lg:text-6xl font-bold mt-5 leading-[1.02]">The all-in-one acquisition operating system for <span className="italic text-accent">creative finance</span> & commercial investors.</h1>
            <p className="text-muted-foreground text-lg mt-6 max-w-2xl mx-auto">Start with a 7-day free trial. No long-term contract — upgrade, downgrade, or cancel anytime.</p>

            <div className="inline-flex items-center gap-1 mt-8 bg-secondary rounded-full p-1" data-testid="billing-toggle">
              <button onClick={() => setAnnual(false)} data-testid="toggle-monthly" className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${!annual ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Monthly</button>
              <button onClick={() => setAnnual(true)} data-testid="toggle-annual" className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${annual ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Annual <span className="text-accent">· save ~25%</span></button>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid lg:grid-cols-3 gap-6 items-start">
          {PLANS.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              data-testid={`plan-${p.id}`}
              className={`relative rounded-lg border p-8 flex flex-col ${p.accent ? "bg-primary text-primary-foreground border-primary shadow-xl lg:-mt-4 lg:mb-4" : "bg-card border-border"}`}>
              {p.badge && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1"><Star className="w-3 h-3" /> {p.badge}</span>}
              <div className={`text-xs uppercase tracking-[0.2em] ${p.accent ? "text-accent" : "text-muted-foreground"}`}>{p.name}</div>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-display text-4xl font-bold tabnum">{annual ? p.yearly : p.monthly}</span>
                {!p.contact && <span className={`text-sm mb-1 ${p.accent ? "text-white/60" : "text-muted-foreground"}`}>/{annual ? "yr" : "mo"}</span>}
              </div>
              <div className={`text-xs mt-1 ${p.accent ? "text-white/50" : "text-muted-foreground"}`}>{annual ? p.yearlyNote : p.tagline}</div>
              {annual && p.annualSaving && (
                <span data-testid={`annual-saving-${p.id}`} className="inline-flex items-center gap-1 mt-3 text-[11px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-600 border border-emerald-500/30 rounded-full px-2.5 py-1 w-fit">
                  Save {p.annualSaving}/yr
                </span>
              )}

              {p.contact
                ? <a href="mailto:team@galahadji.com?subject=Enterprise%20Plan%20Inquiry" data-testid={`cta-${p.id}`} className={`mt-6 w-full text-center rounded-md px-5 py-3 text-sm font-semibold transition-colors ${p.accent ? "bg-accent text-accent-foreground hover:bg-accent/90" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}>{p.cta}</a>
                : <button onClick={() => startCheckout(p)} disabled={loadingPlan === p.id || checking} data-testid={`cta-${p.id}`} className={`group mt-6 w-full flex items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-semibold transition-colors disabled:opacity-60 ${p.accent ? "bg-accent text-accent-foreground hover:bg-accent/90" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}>{loadingPlan === p.id ? "Starting…" : p.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></button>}

              <ul className="mt-8 space-y-3 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.accent ? "text-accent" : "text-emerald-600"}`} />
                    <span className={p.accent ? "text-white/90" : ""}>{f}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-8 max-w-2xl mx-auto">We limit usage, never software quality. Explorer caps at 100 leads, 10 analyses/month and one user with no commercial underwriting. Professional unlocks unlimited leads & analyses plus every creative-finance and commercial module. Enterprise adds team management, API access, advanced reporting and priority support. A payment method may be required to begin the trial; billing starts only after your 7 days end.</p>
      </section>

      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-accent uppercase tracking-[0.25em] text-xs font-medium">Partner Program</span>
            <h2 className="font-marketing text-3xl sm:text-4xl font-bold mt-4">Grow with Galahadji.</h2>
            <p className="text-white/60 mt-4">Two ways to partner — earn on referrals, or run the platform under your own brand.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <div className="rounded-lg border border-white/15 bg-white/5 p-8" data-testid="partner-affiliate">
              <Handshake className="w-8 h-8 text-accent" />
              <h3 className="font-display text-xl font-bold mt-4">Affiliate Partner</h3>
              <p className="text-white/60 mt-2 text-sm leading-relaxed">Free to join or by application.</p>
              <ul className="mt-5 space-y-2.5 text-sm text-white/90">
                <li className="flex gap-2.5"><Check className="w-4 h-4 text-accent mt-0.5 shrink-0" /> Recurring 20–30% commission for referrals</li>
                <li className="flex gap-2.5"><Check className="w-4 h-4 text-accent mt-0.5 shrink-0" /> Paid while the customer stays active</li>
                <li className="flex gap-2.5"><Check className="w-4 h-4 text-accent mt-0.5 shrink-0" /> No intellectual-property transfer</li>
              </ul>
              <a href="mailto:partners@galahadji.com?subject=Affiliate%20Partner" data-testid="cta-affiliate" className="inline-flex items-center gap-2 mt-6 text-accent text-sm font-medium hover:underline">Apply to join <ArrowRight className="w-4 h-4" /></a>
            </div>
            <div className="rounded-lg border border-white/15 bg-white/5 p-8" data-testid="partner-whitelabel">
              <Building2 className="w-8 h-8 text-accent" />
              <h3 className="font-display text-xl font-bold mt-4">White-Label / Reseller Partner</h3>
              <p className="text-white/60 mt-2 text-sm leading-relaxed">For established organizations offering the platform under their own brand.</p>
              <ul className="mt-5 space-y-2.5 text-sm text-white/90">
                <li className="flex gap-2.5"><Check className="w-4 h-4 text-accent mt-0.5 shrink-0" /> One-time onboarding fee + monthly platform fee</li>
                <li className="flex gap-2.5"><Check className="w-4 h-4 text-accent mt-0.5 shrink-0" /> Custom domain & branding</li>
                <li className="flex gap-2.5"><Check className="w-4 h-4 text-accent mt-0.5 shrink-0" /> Client management tools & higher minimum subscription</li>
              </ul>
              <a href="mailto:partners@galahadji.com?subject=White-Label%20Partner" data-testid="cta-whitelabel" className="inline-flex items-center gap-2 mt-6 text-accent text-sm font-medium hover:underline">Request details <ArrowRight className="w-4 h-4" /></a>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-20 text-center">
        <Sparkles className="w-8 h-8 text-accent mx-auto" />
        <h2 className="font-display text-2xl font-bold mt-4">Ready to run your acquisitions on one system?</h2>
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          <Link to="/login" data-testid="pricing-bottom-cta" className="bg-accent text-accent-foreground px-7 py-3.5 rounded-md font-semibold hover:bg-accent/90 transition-colors">Start 7-day trial</Link>
          <Link to="/" className="border border-border px-7 py-3.5 rounded-md font-medium hover:bg-secondary transition-colors">Back to Home</Link>
        </div>
      </section>

      <footer className="bg-primary text-white/50 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row justify-between gap-4 text-sm">
          <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-accent" /> Galahadji Acquisitions Inc · Las Vegas, NV</div>
          <div>© {new Date().getFullYear()} Galahadji. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
